import { AddIcon, AddressIcon, DeleteIcon } from 'cokitchen-icons';
import LocalModal from 'components/base/local-modal';
import React, { useEffect, useMemo, useState } from 'react';
import { ROUTE_KEYS, STORAGE_KEYS } from 'utils/constants';
import { Button, Input, ToastMessage } from '@cokitchen/cokitchen-components';
import { Bars, ThreeDots } from 'react-loader-spinner';
import './styles.scss';
import useOrdersContext from 'contexts/orders.context';
import useDashboardContext from 'contexts/dashboard.context';
import BulkOrderService from 'services/bulk-order.service';
import AddressItem from 'components/address-item';
import { apiService } from 'services/base.service';
import { toast } from 'react-toastify';
import {
  SwipeableList,
  Type as ListType,
  SwipeableListItem,
  TrailingActions,
  SwipeAction
} from 'react-swipeable-list';
import 'react-swipeable-list/dist/styles.css';
import groupOrderService from 'services/group-order.service';
import { CoKitchenType } from 'models/cokitchen.model';
import BusyModal from '../busy-modal';
import { getIsBusy, getNearestCokitchen, handleApiError } from 'utils/functions';
import AddressInput from 'components/base/address-input';
import BranchAddressItem from 'components/branch-address-item';
import { useNavigate } from 'react-router-dom';
import { LocationObjectType } from 'models/location.model';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  setShowDeliveryModal: (value: boolean) => void;
  isBatchOrder: boolean;
};

const LocalTrailingActions = ({
  loading,
  onDeleteAddress
}: {
  loading: boolean;
  onDeleteAddress: () => void;
}) => {
  return (
    <TrailingActions>
      <SwipeAction onClick={onDeleteAddress}>
        <div className='w-[60px] h-[76.5px] py-[18px] text-white select-none flex justify-end'>
          <div
            className={`flex justify-center items-center w-8 h-8 rounded-full ${
              loading ? 'cursor-not-allowed bg-gray-9' : 'bg-red-5 cursor-pointer'
            }`}>
            <DeleteIcon className='h-6' />
          </div>
        </div>
      </SwipeAction>
    </TrailingActions>
  );
};

const ConfirmLocationModal = ({ isOpen, onClose, setShowDeliveryModal, isBatchOrder }: Props) => {
  const {
    savedAddressList,
    addressListLoading,
    refetchSavedAddressList,
    branchAddressesLoading,
    branchAddresses,
    activeCompany,
    refetchBranchAddresses
  } = useDashboardContext();
  const {
    calculatedOrderForm,
    setcalculatedOrderForm,
    currentAddress,
    saveAddressloading,
    currentAddressLoading,
    handleSaveAddress,
    setCoordinates,
    locationAccess
  } = useOrdersContext();

  const navigate = useNavigate();

  const [formattedCurrentAddress, setFormattedCurrentAddress] = useState<LocationObjectType>();
  const [showAddNewAddress, setShowAddNewAddress] = useState(false);
  const [value, setValue] = useState<string>('');
  const [selectedValue, setSelectedValue] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [selectedAddress, setSelectedAddress] = useState<LocationObjectType>();
  const [loading, setLoading] = useState(false);
  const [sortedAddresses, setSortedAddress] = useState<LocationObjectType[]>([]);
  const [cokitchen, setCokitchen] = useState<CoKitchenType>();
  const [isCheckingCokitchen, setIsCheckingCokitchen] = useState(false);
  const [showCokitchenIsBusy, setShowCokitchenIsBusy] = useState(false);

  const DEFAULT_PHONE_NUMBER = localStorage.getItem(STORAGE_KEYS.PHONE_NUMBER) || '';
  const COKITCHEN_LEKKI = 'Cokitchen Lekki';

  const addNewAddress = async () => {
    await handleSaveAddress(value, 'Other');
    setShowAddNewAddress(false);
    setName('');
    setValue('');
    setSelectedValue('');
  };

  useMemo(() => {
    if (savedAddressList.length > 0) {
      setSortedAddress(groupOrderService.RearrangeLocations(savedAddressList));
    }
  }, [savedAddressList]);

  const handleUpdateCalculatedOrderForm = () => {
    if (selectedAddress) {
      setcalculatedOrderForm({
        ...calculatedOrderForm,
        address_details: {
          name: selectedAddress.name ?? '',
          address_line: selectedAddress.address,
          city: selectedAddress.address.split(',')[1] ?? '',
          phone_number: DEFAULT_PHONE_NUMBER,
          building_number: selectedAddress.street_number ?? ''
        },
        lat: selectedAddress.lat.toString(),
        lng: selectedAddress.lng.toString()
      });
      setCoordinates({ lat: selectedAddress.lat.toString(), lng: selectedAddress.lng.toString() });
    }
  };

  const onAddressConfirm = async () => {
    if (!selectedAddress) return;
    const cokitchen: CoKitchenType = await getNearestCokitchen(
      selectedAddress,
      setIsCheckingCokitchen
    );
    if (cokitchen.name !== COKITCHEN_LEKKI) {
      toast(<ToastMessage type='error' message={'Location not supported'} />);
      onClose();
      return;
    }

    const cokitchenIsBusy = getIsBusy(cokitchen);
    if (cokitchenIsBusy) {
      setCokitchen(cokitchen);
      return setShowCokitchenIsBusy(true);
    }
    handleUpdateCalculatedOrderForm();
    onClose();
    if (isBatchOrder) {
      navigate(ROUTE_KEYS.BATCHED_ORDERS_BRANDS);
    } else {
      setShowDeliveryModal(true);
    }
  };

  useEffect(() => {
    if (currentAddress !== '') {
      const getFormattedAddress = async () => {
        const formattedAddress = await BulkOrderService.FormatAddress(currentAddress, 'Default');
        setFormattedCurrentAddress(formattedAddress);
      };
      getFormattedAddress();
    }
  }, [currentAddress]);

  useEffect(() => {
    if (formattedCurrentAddress) {
      setSelectedAddress(formattedCurrentAddress);
    } else {
      setSelectedAddress(sortedAddresses[0]);
    }
  }, [formattedCurrentAddress]);

  useEffect(() => {
    if (selectedValue !== '') {
      setValue(selectedValue);
    }
  }, [selectedValue]);
  const handleDeleteAddress = async (id: string) => {
    if (loading) return;
    try {
      setLoading(true);
      await apiService.delete(`user-saved-address/${id}`, {
        baseURL: process.env.REACT_APP_ORDER_API_URL
      });

      toast(<ToastMessage message='Address deleted successfully' type='success' />);
      await refetchSavedAddressList();
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBranchAddress = async (id: string) => {
    if (loading || !activeCompany) return;
    try {
      setLoading(true);
      await apiService.delete(`branches/${activeCompany.id}/${id}`);
      toast(<ToastMessage message='Address deleted successfully' type='success' />);
      refetchBranchAddresses();
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <LocalModal isOpen={isOpen} onRequestClose={onClose} title='Confirm Location'>
      {showAddNewAddress ? (
        <div className='w-full address_form'>
          <div className='flex flex-col gap-3'>
            <AddressInput
              label='Provide address'
              placeholder='Enter address'
              value={value}
              onChange={setValue}
            />
            <Input
              design='bordered'
              label='Location tag'
              placeholder='e.g head office, leisure etc'
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className='w-full flex gap-4 justify-end'>
            <Button
              variant='block'
              color='gray'
              className='mt-8 h-[41px] w-[139px] !bg-[#F2F2F2] !border-[#F2F2F2] !text-[#2E3031]'
              onClick={() => setShowAddNewAddress(false)}>
              Cancel
            </Button>
            <Button
              variant='block'
              color='primary'
              className='mt-8 h-[41px] w-[139px]'
              disabled={selectedValue === ''}
              loading={saveAddressloading}
              onClick={addNewAddress}>
              Save
            </Button>
          </div>
        </div>
      ) : (
        <div className='w-full overflow-x-scroll no-scrollbar'>
          {locationAccess && (
            <div
              className='w-full flex items-center justify-between gap-3 py-[18px] border-b-[0.8px] border-b-[#E7E8E9] cursor-pointer'
              onClick={() => setSelectedAddress(formattedCurrentAddress)}>
              <div className='flex gap-3 items-center w-[90%]'>
                <AddressIcon />
                <div className='w-[65%]'>
                  <p className='text-[#2E3031] text-sm font-medium capitalize'>Current Location</p>
                  {currentAddressLoading ? (
                    <ThreeDots
                      visible={true}
                      height='20'
                      width='50'
                      color='#E60A2B'
                      radius='9'
                      ariaLabel='three-dots-loading'
                    />
                  ) : (
                    <p className='text-[#5E6164] text-sm line-clamp-1'>
                      {`
                     ${
                       formattedCurrentAddress?.street_number !== null
                         ? `${formattedCurrentAddress?.street_number}, `
                         : ''
                     }${formattedCurrentAddress?.address}
                      `}
                    </p>
                  )}
                </div>
              </div>
              <span
                className={`ml-auto w-[14px] h-[14px] rounded-full ${
                  selectedAddress === formattedCurrentAddress
                    ? 'border-[3.8px] border-[#E60A2B]'
                    : 'border-[1.3px] border-[#D6D8E0]'
                }`}
              />
            </div>
          )}
          <div className='w-full'>
            {addressListLoading || branchAddressesLoading ? (
              <div className='w-full h-[20vh] flex justify-center items-center'>
                <Bars color='#E60A2B' height='50' width='50' />
              </div>
            ) : (
              <div className='w-full min-h-[5vh] max-h-[30vh] overflow-y-scroll no-scrollbar'>
                {savedAddressList.length === 0 && branchAddresses.length === 0 ? (
                  <div>
                    <p className='text-sm text-gray-10'>No saved address</p>
                  </div>
                ) : (
                  <SwipeableList type={ListType.IOS} threshold={0.5}>
                    {branchAddresses.map((address, i) => (
                      <SwipeableListItem
                        key={i}
                        scrollStartThreshold={0.5}
                        trailingActions={
                          <LocalTrailingActions
                            loading={loading}
                            onDeleteAddress={() => handleDeleteBranchAddress(address.id)}
                          />
                        }>
                        <BranchAddressItem
                          address={address}
                          isSelected={selectedAddress?.id === address.id}
                          setSelectedAddress={setSelectedAddress}
                          number={i + 1}
                        />
                      </SwipeableListItem>
                    ))}
                    {sortedAddresses.map((address, i) => {
                      return (
                        <SwipeableListItem
                          key={i}
                          scrollStartThreshold={0.5}
                          trailingActions={
                            <LocalTrailingActions
                              loading={loading}
                              onDeleteAddress={() => handleDeleteAddress(address?.id ?? '')}
                            />
                          }>
                          <AddressItem
                            address={address}
                            selectedAddress={selectedAddress}
                            setSelectedAddress={setSelectedAddress}
                          />
                        </SwipeableListItem>
                      );
                    })}
                  </SwipeableList>
                )}
              </div>
            )}
          </div>
          <div
            className='mt-6 flex gap-3 text-[#3FB827] items-center cursor-pointer'
            onClick={() => setShowAddNewAddress(true)}>
            <AddIcon className='w-[18px] h-[18px]' />
            <p className='text-sm'>Add new location</p>
          </div>

          <div className='w-full flex gap-4 justify-end'>
            <Button
              variant='block'
              color='gray'
              className='mt-8 h-[41px] w-[139px] !bg-[#F2F2F2] !border-[#F2F2F2] !text-[#2E3031]'
              onClick={() => onClose()}>
              Cancel
            </Button>

            <Button
              variant='block'
              color='primary'
              className='mt-8 h-[41px] w-[139px]'
              loading={isCheckingCokitchen}
              disabled={selectedAddress === undefined}
              onClick={onAddressConfirm}>
              Confirm
            </Button>
          </div>
          {cokitchen ? (
            <BusyModal
              open={showCokitchenIsBusy}
              onClose={() => setShowCokitchenIsBusy(false)}
              data={cokitchen}
            />
          ) : null}
        </div>
      )}
    </LocalModal>
  );
};

export default ConfirmLocationModal;
